/**
 * Users API service
 * Handles all user-related API operations
 */

import { httpClient } from '@/utils/httpClient';
import { config } from '@/config';
import { notifyError, notifySuccess } from '@/utils/toast';
import type {
    APIUser,
    APIUserRequest,
    APIUserDocument,
    DRFPaginatedResponse,
} from '@/types';

const buildQuery = (params: Record<string, unknown>): string => {
    const usp = new URLSearchParams();
    Object.entries(params).forEach(([k, v]) => {
        if (v !== undefined && v !== null) usp.append(k, String(v));
    });
    const s = usp.toString();
    return s ? `?${s}` : '';
};

export interface UserListParams {
    page?: number;
    page_size?: number;
    is_active?: boolean;
    name?: string;
    role?: number;
}

export const usersService = {
    async list(params?: UserListParams): Promise<DRFPaginatedResponse<APIUser>> {
        const qs = buildQuery((params ?? {}) as Record<string, unknown>);
        return httpClient.get<DRFPaginatedResponse<APIUser>>(`/users/${qs}`);
    },

    async getById(id: number): Promise<APIUser> {
        return httpClient.get<APIUser>(`/users/${id}/`);
    },

    async create(data: APIUserRequest): Promise<APIUser> {
        return httpClient.post<APIUser, APIUserRequest>('/users/', data, {
            successTitle: 'Cliente creado',
            successMessage: 'El cliente se registró correctamente.',
            errorTitle: 'Error en cliente',
        });
    },

    async update(id: number, data: APIUserRequest): Promise<APIUser> {
        return httpClient.put<APIUser, APIUserRequest>(`/users/${id}/`, data, {
            successTitle: 'Cliente actualizado',
            successMessage: 'Los datos del cliente se guardaron.',
            errorTitle: 'Error en cliente',
        });
    },

    async patch(id: number, data: Partial<APIUserRequest>): Promise<APIUser> {
        return httpClient.patch<APIUser, Partial<APIUserRequest>>(`/users/${id}/`, data, {
            successTitle: 'Cliente actualizado',
            successMessage: 'Se aplicaron cambios en la ficha del cliente.',
            errorTitle: 'Error en cliente',
        });
    },

    async remove(id: number): Promise<void> {
        return httpClient.delete<void>(`/users/${id}/`, {
            successTitle: 'Cliente eliminado',
            successMessage: 'El cliente se eliminó correctamente.',
            errorTitle: 'Error en cliente',
        });
    },

    /**
     * Create user with FormData (required by backend for multipart/form-data)
     * POST /users/  — sends as multipart/form-data instead of JSON
     */
    async createFormData(data: Partial<APIUserRequest>): Promise<APIUser> {
        try {
            const formData = new FormData();
            Object.entries(data).forEach(([key, value]) => {
                if (value !== undefined && value !== null) {
                    formData.append(key, String(value));
                }
            });
            const token = localStorage.getItem('access_token');
            const response = await fetch(`${config.api.baseUrl}/users/`, {
                method: 'POST',
                headers: token ? { Authorization: `Bearer ${token}` } : {},
                body: formData,
            });
            if (!response.ok) throw new Error(`Create user failed: ${response.status}`);

            notifySuccess('Usuario creado');
            return response.json() as Promise<APIUser>;
        } catch (error) {
            notifyError(error, 'No se pudo crear el usuario');
            throw error;
        }
    },

    /**
     * Update user with FormData (required by backend for multipart/form-data)
     * PUT /users/{id}/  — sends as multipart/form-data instead of JSON
     */
    async updateFormData(id: number, data: Partial<APIUserRequest>): Promise<APIUser> {
        try {
            const formData = new FormData();
            Object.entries(data).forEach(([key, value]) => {
                if (value !== undefined && value !== null) {
                    formData.append(key, String(value));
                }
            });
            const token = localStorage.getItem('access_token');
            const response = await fetch(`${config.api.baseUrl}/users/${id}/`, {
                method: 'PATCH',
                headers: token ? { Authorization: `Bearer ${token}` } : {},
                body: formData,
            });
            if (!response.ok) throw new Error(`Update user failed: ${response.status}`);

            notifySuccess('Usuario actualizado');
            return response.json() as Promise<APIUser>;
        } catch (error) {
            notifyError(error, 'No se pudo actualizar el usuario');
            throw error;
        }
    },

    /**
     * Export users to Excel
     * GET /users/export-excel/
     * Uses fetch directly to handle Blob response
     */
    async exportExcel(params?: UserListParams): Promise<Blob> {
        try {
            const qs = buildQuery((params ?? {}) as Record<string, unknown>);
            const token = localStorage.getItem('access_token');
            const response = await fetch(`${config.api.baseUrl}/users/export-excel/${qs}`, {
                headers: token ? { Authorization: `Bearer ${token}` } : {},
            });
            if (!response.ok) throw new Error('Export failed');

            notifySuccess('Exportación completada', 'El archivo se ha generado correctamente.');
            return response.blob();
        } catch (error) {
            notifyError(error, 'No se pudo exportar el archivo');
            throw error;
        }
    },

    // ── Document sub-resource  GET|POST  /users/{id}/documents/ ──────────────

    /**
     * GET /users/{id}/documents/
     * Returns the list of documents attached to a user.
     */
    async listDocuments(userId: number): Promise<APIUserDocument[]> {
        return httpClient.get<APIUserDocument[]>(`/users/${userId}/documents/`);
    },

    /**
     * POST /users/{id}/documents/
     * Uploads a new document for the user (multipart/form-data).
     */
    async uploadDocument(
        userId: number,
        file: File,
        documentType: string,
    ): Promise<APIUserDocument> {
        try {
            const formData = new FormData();
            formData.append('file', file);
            formData.append('type', documentType);
            const token = localStorage.getItem('access_token');
            const response = await fetch(
                `${config.api.baseUrl}/users/${userId}/documents/`,
                {
                    method: 'POST',
                    headers: token ? { Authorization: `Bearer ${token}` } : {},
                    body: formData,
                },
            );
            if (!response.ok)
                throw new Error(`Upload document failed: ${response.status}`);

            notifySuccess('Documento subido');
            return response.json() as Promise<APIUserDocument>;
        } catch (error) {
            notifyError(error, 'No se pudo subir el documento');
            throw error;
        }
    },

    /**
     * DELETE /users/{id}/documents/{documentId}/
     * Soft- or hard-deletes a document.
     */
    async deleteDocument(userId: number, documentId: number): Promise<void> {
        return httpClient.delete<void>(
            `/users/${userId}/documents/${documentId}/`,
            {
                successTitle: 'Documento eliminado',
                successMessage: 'El documento del cliente se eliminó correctamente.',
                errorTitle: 'Error en documento',
            }
        );
    },
};

export default usersService;
